import { cloneDeep, orderBy } from "lodash";

import { toast } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

export interface WithUid {
  uid?: number | null;
}

type ReOrderItemsUidType = WithUid & { gatewayName?: string };

export const BOX = "BOX";

/**
 * Replaces the gateway name with the provided UID.
 * If the gateway name is not available, returns a dash ("-").
 * @param gateway - The gateway object.
 * @param uid - The UID to replace in the gateway name.
 * @returns The modified gateway name or a dash ("-").
 */
export const replaceGatewayName = (gatewayName: string, lastUid: number, uid: number): string => {
  return gatewayName
    ? gatewayName.includes(`${BOX}${lastUid}`)
      ? gatewayName.replace(`${BOX}${lastUid}`, `${BOX}${uid}`)
      : gatewayName
    : "-";
};

export const reOrderItemsUid = <T extends ReOrderItemsUidType>(items: T[], startUid: number, targetUid: number) => {
  const indexWithStartUid = items.findIndex(item => item.uid === startUid);
  const indexWithTargetUid = items.findIndex(item => item.uid === targetUid);

  if (indexWithStartUid === indexWithTargetUid) return items;
  else if (indexWithTargetUid === -1 || indexWithStartUid === -1) {
    toast.error(m.element_with_uid_not_found({ n: startUid, n2: targetUid }));
    return;
  }
  const copyItems = cloneDeep(items);
  const startItem = copyItems[indexWithStartUid];
  if (!startItem) return copyItems;

  const startItemUid = startItem.uid;
  if (startItemUid == null) return copyItems;

  if (startItem.gatewayName) {
    startItem.gatewayName = replaceGatewayName(startItem.gatewayName, startItemUid, targetUid);
  }
  startItem.uid = targetUid;

  if (startUid > targetUid) {
    for (let i = indexWithStartUid - 1; i >= indexWithTargetUid; i--) {
      const item = copyItems[i];
      if (!item) continue;
      const uid = item.uid;
      if (uid == null) continue;
      if (item.gatewayName) {
        item.gatewayName = replaceGatewayName(item.gatewayName, uid, uid + 1);
      }
      item.uid = uid + 1;
    }
  } else if (startUid < targetUid) {
    for (let i = indexWithStartUid + 1; i <= indexWithTargetUid; i++) {
      const item = copyItems[i];
      if (!item) continue;
      const uid = item.uid;
      if (uid == null) continue;
      if (item.gatewayName) {
        item.gatewayName = replaceGatewayName(item.gatewayName, uid, uid - 1);
      }
      item.uid = uid - 1;
    }
  }

  return orderBy(copyItems, ["uid"]);
};
